import { UnapprovedTag } from "./UnapprovedSupplierDialog";
import React from "react";
import { Plus, Trash2, GripVertical, Paperclip, Loader2, FileUp } from "lucide-react";
import { api, gbp, openFile } from "../../lib/apiClient";
import { Section } from "./layout";
import { useRowDrag } from "./useRowDrag";
import { MarkupCell } from "./MarkupCell";
import { numVal, parseNum, tmpId } from "./utils";

/**
 * AccessHireTable — Scissor / Boom / Scaffold hire as its own cost section.
 *
 * Columns (L→R): drag · Product · Type · Actual product · Days on hire · Qty · Cost ex VAT · Markup % · Markup £ · Line Total · delete
 *
 * Type options depend on the selected Product (admin-managed via Settings → Access Hire).
 */
export function AccessHireTable({
  items,
  onChange,
  ahProducts = [],
  markupOptions = [],
  userInitials = "",
  defaultMarkup = 35,
  isAdmin = false,
  minMarkup = null,
  previewItems,
  previewTotal = 0,
  storageKey,
  quoteId = null,
  pendingAttachments = [],
  setPendingAttachments = null,
  onPdfFile,
  onSupplierBlur,
  supplierChecksOn,
  pdfUploading = false,
  pdfMsg = "",
  pdfError = "",
}) {
  const drag = useRowDrag(items || [], onChange, "access");
  const fileRef = React.useRef(null);
  const pdfRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [attachInfo, setAttachInfo] = React.useState("");

  const onPdfSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    if (onPdfFile) await onPdfFile(f);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setAttachInfo(`Attaching ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = quoteId
        ? `/quotes/${quoteId}/attachments?scope=access_hire`
        : `/uploads/draft?scope=access_hire`;
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Draft uploads need to be tracked locally so they're linked on save
      if (!quoteId && setPendingAttachments) {
        setPendingAttachments((prev) => [...prev, data]);
      }
      const subj = (data.kind === "msg" || data.kind === "eml") && data.parsed_email?.subject
        ? ` · email "${data.parsed_email.subject}"`
        : "";
      const draftBadge = !quoteId ? " (will attach when you Save)" : "";
      setAttachInfo(`✓ Attached · ${data.original_name}${subj}${draftBadge}`);
      window.setTimeout(() => setAttachInfo(""), 6000);
    } catch (ex) {
      setAttachInfo(ex?.response?.data?.detail || "Attach failed");
    } finally {
      setUploading(false);
    }
  };

  const removeDraft = async (fileId) => {
    try { await api.delete(`/uploads/draft/${fileId}`); } catch { /* ignore */ }
    if (setPendingAttachments) {
      setPendingAttachments((prev) => prev.filter((a) => a.id !== fileId));
    }
  };

  const setRow = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addRow = () => onChange([
    ...(items || []),
    {
      id: tmpId(),
      product: "",
      type: "",
      actual_product: "",
      days: 1,
      quantity: 1,
      supplier_cost: 0,
      markup_percent: defaultMarkup,
    },
  ]);

  const typesFor = (productName) => {
    const p = ahProducts.find((x) => x.name === productName);
    return p?.types || [];
  };

  const lineFor = (idx, ln) => {
    const fromPreview = previewItems?.[idx];
    if (fromPreview && fromPreview.line_total != null) return fromPreview;
    // `days` is INFORMATIONAL only — it tells the office how long the lift is
    // needed on site. Suppliers quote the TOTAL hire fee (qty × supplier cost),
    // so multiplying by days would double-count.
    const qty = Number(ln.quantity || 0);
    const cost = Number(ln.supplier_cost || 0);
    const mp = Number(ln.markup_percent || 0) / 100;
    const sub = qty * cost;
    return { subtotal_raw: sub, markup_value: sub * mp, line_total: sub * (1 + mp) };
  };

  return (
    <Section
      title="Access Hire"
      subtitle="Scissor / boom / scaffold hire — total qty × supplier cost (days is informational only)"
      collapsible
      storageKey={storageKey}
      action={
        <div className="flex items-center gap-2">
          <input
            ref={pdfRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={onPdfSelected}
            data-testid="access-pdf-upload-input"
          />
          <button
            onClick={() => pdfRef.current?.click()}
            disabled={pdfUploading}
            data-testid="access-upload-supplier-pdf-button"
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 px-3 h-8 border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white disabled:opacity-60"
            title="AI-extract a hire quote PDF (days, qty, daily rate) into a new row"
          >
            {pdfUploading ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
            {pdfUploading ? "Reading PDF…" : "Upload Supplier PDF"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,image/*,.msg,.eml"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) await handleUpload(f);
            }}
            data-testid="access-attach-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="access-attach-button"
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 px-3 h-8 border border-slate-300 text-slate-600 hover:border-slate-900 hover:text-slate-900 disabled:opacity-60"
            title="Attach a supplier doc, photo, or Outlook email (.msg / .eml) — appended to the exported Quote Matrix PDF without auto-extracting."
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
            {uploading ? "Uploading…" : "Attach only"}
          </button>
          <button
            onClick={addRow}
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:text-slate-900 text-slate-600"
            data-testid="add-access-hire-button"
          >
            <Plus size={14} /> Add line
          </button>
        </div>
      }
    >
      {(attachInfo || pdfMsg || pdfError || (pendingAttachments && pendingAttachments.length > 0)) && (
        <div className="mb-2 space-y-1" data-testid="access-attach-status">
          {pdfError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5" data-testid="access-pdf-error">{pdfError}</div>
          )}
          {pdfMsg && !pdfError && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5" data-testid="access-pdf-info">{pdfMsg}</div>
          )}
          {attachInfo && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-1.5" data-testid="access-attach-info">
              {attachInfo}
            </div>
          )}
          {!quoteId && pendingAttachments && pendingAttachments.length > 0 && (
            <div className="text-[11px] text-slate-500 px-1 flex flex-wrap gap-1.5 items-center">
              <span className="uppercase tracking-wider font-semibold text-[10px] text-brand-orange">Pending {pendingAttachments.length}</span>
              {pendingAttachments.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-100 text-slate-700"
                  data-testid={`access-pending-${a.id}`}
                >
                  <Paperclip size={10} />
                  {a.original_name}
                  <button
                    onClick={() => removeDraft(a.id)}
                    className="hover:text-red-600"
                    aria-label="Remove pending attachment"
                  >×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <div>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full text-sm table-fixed min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th style={{ width: 28 }} className="px-1 py-2"></th>
              {[
                { l: "Product", w: "15%" },
                { l: "Type", w: "12%" },
                { l: "Actual product", w: "18%" },
                { l: "Days", w: "6%", right: true },
                { l: "Qty", w: "6%", right: true },
                { l: "Cost ex VAT", w: "10%", right: true },
                { l: "Markup %", w: "8%", right: true },
                { l: "Markup £", w: "8%", right: true },
                { l: "Line Total", w: "10%", right: true },
                { l: "", w: "36px" },
              ].map((h) => (
                <th
                  key={h.l || "actions"}
                  style={{ width: h.w }}
                  className={`px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 ${h.right ? "text-right" : "text-left"}`}
                >{h.l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(items || []).length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-400 text-sm">No access hire yet.</td></tr>
            )}
            {(items || []).map((ln, idx) => {
              const calc = lineFor(idx, ln);
              const types = typesFor(ln.product);
              return (
                <React.Fragment key={ln.id || idx}>
                <tr className="border-b border-slate-200" {...drag.dragProps(idx)}>
                  <td className="px-1 py-1 align-middle text-slate-400 hover:text-slate-700" {...drag.handleProps(idx)}>
                    <GripVertical size={14} />
                  </td>
                  <td className="px-1 py-1 align-middle">
                    <select
                      value={ln.product || ""}
                      onChange={(e) => {
                        // Reset type when product changes (old type may not be valid)
                        const nextTypes = typesFor(e.target.value);
                        const keepType = ln.type && nextTypes.includes(ln.type) ? ln.type : "";
                        setRow(idx, { product: e.target.value, type: keepType });
                      }}
                      className="qb-input h-9 w-full"
                      data-testid={`access-product-${idx}`}
                    >
                      <option value="">—</option>
                      {ahProducts.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-1">
                    <select
                      value={ln.type || ""}
                      onChange={(e) => setRow(idx, { type: e.target.value })}
                      className="qb-input h-9"
                      data-testid={`access-type-${idx}`}
                      disabled={!ln.product || types.length === 0}
                    >
                      <option value="">{ln.product ? "—" : ""}</option>
                      {types.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-1">
                    <input
                      value={ln.actual_product || ""}
                      onChange={(e) => setRow(idx, { actual_product: e.target.value })}
                      className="qb-input h-9"
                      data-testid={`access-actual-${idx}`}
                      placeholder="Supplier model / spec"
                    />
                    <input
                      value={ln.supplier_name || ""}
                      onChange={(e) => setRow(idx, { supplier_name: e.target.value })}
                      onFocus={(e) => { e.target.dataset.orig = e.target.value; }}
                      onBlur={(e) => { if (e.target.value !== e.target.dataset.orig) onSupplierBlur?.(ln, e.target.value); }}
                      className="qb-input h-8 mt-1 text-xs"
                      data-testid={`access-supplier-${idx}`}
                      list={supplierChecksOn ? "supplier-options-access_hire" : undefined}
                      autoComplete="off"
                      placeholder="Hire supplier (e.g. Nationwide Platforms)"
                    />
                    <UnapprovedTag rec={ln.unapproved_supplier} testid={`access-unapproved-tag-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="0.5" min="0"
                      value={numVal(ln.days)}
                      onChange={(e) => setRow(idx, { days: parseNum(e.target.value) })}
                      className="qb-input h-9 font-mono text-right"
                      data-testid={`access-days-${idx}`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="1" min="0"
                      value={numVal(ln.quantity)}
                      onChange={(e) => setRow(idx, { quantity: parseNum(e.target.value) })}
                      className="qb-input h-9 font-mono text-right"
                      data-testid={`access-qty-${idx}`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="0.01" min="0"
                      value={numVal(ln.supplier_cost)}
                      onChange={(e) => setRow(idx, { supplier_cost: parseNum(e.target.value) })}
                      className="qb-input h-9 font-mono text-right"
                      data-testid={`access-cost-${idx}`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <MarkupCell
                      value={ln.markup_percent}
                      presets={markupOptions}
                      userInitials={userInitials}
                      overrideBy={ln.markup_override_by}
                      overrideAt={ln.markup_override_at}
                      onChangeWithAudit={(pct, audit) => setRow(idx, { markup_percent: pct, ...audit })}
                      testId={`access-markup-${idx}`}
                      isAdmin={isAdmin}
                      minPercent={minMarkup}
                    />
                  </td>
                  <td className="px-2 py-1 font-mono text-right whitespace-nowrap text-slate-500">{gbp(calc.markup_value || 0)}</td>
                  <td className="px-2 py-1 font-mono text-right whitespace-nowrap font-semibold">{gbp(calc.line_total || 0)}</td>
                  <td className="px-2 py-1 text-right">
                    <button onClick={() => removeRow(idx)} className="text-slate-400 hover:text-red-600" data-testid={`remove-access-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
                {ln.source_pdf_id && (
                  <tr className="border-b border-slate-200">
                    <td></td>
                    <td colSpan={10} className="px-2 pb-2 pt-0">
                      <a
                        href="#" onClick={(e) => { e.preventDefault(); openFile(ln.source_pdf_id, quoteId); }}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-brand-blue font-semibold hover:underline truncate max-w-full"
                        data-testid={`access-source-pdf-${idx}`}
                        title={ln.source_pdf_name || "View source PDF"}
                      >
                        <Paperclip size={10} className="shrink-0" />
                        <span className="truncate">{ln.source_pdf_name || "View source PDF"}</span>
                      </a>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      {(items || []).length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm bg-slate-50 rounded-lg px-4 py-2">
          <span className="text-slate-600">Access hire total</span>
          <span className="font-mono font-semibold text-brand-blue" data-testid="access-hire-total">{gbp(previewTotal || 0)}</span>
        </div>
      )}
    </Section>
  );
}
